import {
  AbstractControl,
  AsyncValidatorFn,
  ValidationErrors
} from "@angular/forms";
import { Observable } from "rxjs";
import { map, mergeMap, shareReplay } from "rxjs/operators";
import { I18nService } from "src/app/i18n/services";
import { Circulation, CirculationId } from "src/app/interfaces/circulation";
import { StepId } from "src/app/interfaces/step";
import { CirculationFacade } from "src/app/services/circulation.facade";

export class DueDateInRangeValidator {
  circulation: Observable<Circulation>;

  constructor(
    private circulationFacade: CirculationFacade,
    private i18nService: I18nService,
    circId: CirculationId,
    private stepId: StepId | null
  ) {
    this.circulation = circulationFacade.getCirculation(circId).pipe(
      shareReplay({
        bufferSize: 1,
        refCount: true
      })
    );
  }

  public static createValidateFn(
    circulationFacade: CirculationFacade,
    i18nService: I18nService,
    circId: CirculationId,
    stepId: StepId | null
  ): AsyncValidatorFn {
    const validator = new DueDateInRangeValidator(
      circulationFacade,
      i18nService,
      circId,
      stepId
    );
    return validator.validate.bind(validator);
  }

  public static isBefore(a: Date, b: Date): boolean {
    if (a.getFullYear() < b.getFullYear()) {
      return true;
    }
    if (a.getFullYear() > b.getFullYear()) {
      return false;
    }
    if (a.getMonth() < b.getMonth()) {
      return true;
    }
    if (a.getMonth() > b.getMonth()) {
      return false;
    }
    if (a.getDate() < b.getDate()) {
      return true;
    }

    return false;
  }

  public static isAfter(a: Date, b: Date): boolean {
    if (a.getFullYear() < b.getFullYear()) {
      return false;
    }
    if (a.getFullYear() > b.getFullYear()) {
      return true;
    }
    if (a.getMonth() < b.getMonth()) {
      return false;
    }
    if (a.getMonth() > b.getMonth()) {
      return true;
    }
    if (a.getDate() <= b.getDate()) {
      return false;
    }

    return true;
  }

  public validate(
    control: AbstractControl
  ): Observable<ValidationErrors | null> {
    const date = <Date>control.value;
    if (!date) {
      return null; // use Validators.required additionally.
    }
    return this.circulation.pipe(
      mergeMap((circ) =>
        circ.getValidDueDateRangeForStep(this.circulationFacade, this.stepId)
      ),
      map((range) => {
        if (DueDateInRangeValidator.isBefore(date, range.from)) {
          return {
            range: {
              before: range.from,
              beforeTxt: this.i18nService.formatDate(range.from, {
                date: "medium"
              })
            }
          };
        }
        if (DueDateInRangeValidator.isAfter(date, range.to)) {
          return {
            range: {
              after: range.to,
              afterTxt: this.i18nService.formatDate(range.to, {
                date: "medium"
              })
            }
          };
        }

        return null; // no errors
      })
    );
  }
}
